<script setup>
/**
 * One blank in the approved message, and where its text comes from.
 *
 * THREE SOURCES, AND THE THIRD ONE IS THE DANGEROUS ONE. "Something you keep about them"
 * and "one of your own details" are filled in per person; "the same words for everyone" is
 * not, and an owner who reaches for it because the first two looked complicated has just
 * sent nine hundred people a message addressed to nobody. So the fixed-text box carries the
 * only warning on this screen that is not about failure: the per-person sources say what
 * happens when the detail is MISSING (that customer is skipped, not sent half a message),
 * which is the fact that makes choosing them safe.
 *
 * SWITCHING SOURCE CLEARS THE VALUE, deliberately and visibly. The htmx original did the
 * same thing by accident — every inactive source's hidden input was hard-coded to `""` —
 * and it is the right behaviour for a reason worth stating: a contact-field key and a
 * literal are not the same kind of thing, and carrying "name" across into a box that will
 * be sent verbatim would put the word *name* in nine hundred messages.
 */
import { computed } from 'vue';

import WasloInput from '../WasloInput.vue';
import WasloSelect from '../WasloSelect.vue';
import { useWasloI18n } from '../../i18n.js';
import { CONTACT_FIELDS } from '../../helpers/broadcast-compose.js';

const props = defineProps({
  /** The template variable this row fills, e.g. `1`. */
  variable: { type: String, required: true },
  /** `{ src, key } | { src: 'literal', value }`. */
  binding: { type: Object, required: true },
  /** The account's own contact details, from `wasloApi.getAudienceFields`. */
  attributes: { type: Array, default: () => [] },
  error: { type: String, default: '' },
});

const emit = defineEmits(['update']);

const { t } = useWasloI18n();

/**
 * "Blank {{2}}", built in script rather than in the template, and it HAS to be: a `}}`
 * inside a mustache closes the interpolation, so the same expression written inline is a
 * compile error — one the harness reports as a blank page. The braces are what the owner
 * sees in the approved text, so they stay in the label.
 */
const blankLabel = computed(() => {
  // P-4.3: the compose page hands this row only the key, so a button is named by its number.
  if (props.variable.startsWith('button:')) {
    return t('WASLO.BROADCASTS.COMPOSE.FILL.BUTTON_BLANK', {
      n: Number(props.variable.slice('button:'.length)) + 1,
    });
  }
  if (props.variable.startsWith('header:')) {
    return t('WASLO.BROADCASTS.COMPOSE.FILL.TITLE_BLANK', {
      name: `{{${props.variable.slice('header:'.length)}}}`,
    });
  }
  return t('WASLO.BROADCASTS.COMPOSE.FILL.BLANK', {
    name: `{{${props.variable}}}`,
  });
});

const sourceOptions = computed(() => [
  { value: 'field', label: t('WASLO.BROADCASTS.COMPOSE.FILL.SOURCES.FIELD') },
  {
    value: 'attr',
    label: t('WASLO.BROADCASTS.COMPOSE.FILL.SOURCES.ATTR'),
    // Offered but unusable is better than silently absent: an owner who expects her own
    // details here needs to be told this workspace keeps none, not left wondering.
    disabled: props.attributes.length === 0,
  },
  {
    value: 'literal',
    label: t('WASLO.BROADCASTS.COMPOSE.FILL.SOURCES.LITERAL'),
  },
]);

const fieldOptions = computed(() =>
  CONTACT_FIELDS.map(key => ({
    value: key,
    label: t(`WASLO.BROADCASTS.COMPOSE.FILL.FIELDS.${key.toUpperCase()}`),
  }))
);

const attributeOptions = computed(() =>
  props.attributes.map(attribute => ({
    value: attribute.key,
    label: attribute.label,
  }))
);

const onSource = src => {
  if (src === props.binding.src) return;
  if (src === 'literal') emit('update', { src: 'literal', value: '' });
  else if (src === 'attr') emit('update', { src: 'attr', key: '' });
  else emit('update', { src: 'field', key: 'name' });
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 outline outline-1 -outline-offset-1 outline-n-weak rounded-xl bg-n-solid-1"
    :data-waslo-binding="variable"
  >
    <p class="mb-0 text-sm font-medium text-n-slate-12">
      {{ blankLabel }}
    </p>

    <div class="grid gap-3 sm:grid-cols-2">
      <WasloSelect
        :model-value="binding.src"
        :options="sourceOptions"
        :label="t('WASLO.BROADCASTS.COMPOSE.FILL.SOURCE')"
        data-waslo-binding-source
        @update:model-value="onSource"
      />

      <WasloSelect
        v-if="binding.src === 'field'"
        :model-value="binding.key"
        :options="fieldOptions"
        :label="t('WASLO.BROADCASTS.COMPOSE.FILL.SOURCES.FIELD')"
        @update:model-value="key => emit('update', { src: 'field', key })"
      />

      <WasloSelect
        v-else-if="binding.src === 'attr'"
        :model-value="binding.key"
        :options="attributeOptions"
        :label="t('WASLO.BROADCASTS.COMPOSE.FILL.ATTR_LABEL')"
        :placeholder="t('WASLO.BROADCASTS.COMPOSE.AUDIENCE.FIELD_PLACEHOLDER')"
        :message="
          attributeOptions.length === 0
            ? t('WASLO.BROADCASTS.COMPOSE.FILL.ATTR_NONE')
            : error
        "
        :message-type="error ? 'error' : 'info'"
        @update:model-value="key => emit('update', { src: 'attr', key })"
      />

      <WasloInput
        v-else
        :model-value="binding.value"
        :label="t('WASLO.BROADCASTS.COMPOSE.FILL.LITERAL_LABEL')"
        :placeholder="t('WASLO.BROADCASTS.COMPOSE.FILL.LITERAL_PLACEHOLDER')"
        :message="error"
        :message-type="error ? 'error' : 'info'"
        data-waslo-binding-literal
        @update:model-value="value => emit('update', { src: 'literal', value })"
      />
    </div>

    <p
      v-if="binding.src !== 'literal'"
      class="mb-0 text-xs text-n-slate-10 max-w-[62ch]"
    >
      {{ t('WASLO.BROADCASTS.COMPOSE.FILL.EMPTY_WARNING') }}
    </p>
  </div>
</template>
